import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@shared/context/AuthContext";
import { useFeatureFlags } from "@shared/hooks/useFeatureFlags";
import type { FeatureAvailabilityResult } from "@shared/hooks/useFeatureFlags";
import { isLocalDevelopmentPreview } from "@shared/config/localDevelopmentPreview";
import {
  NAVIGATION_ROUTES,
  NavArea,
  NavigationRoute,
  RegulatoryPhase,
  canAccessNavigationRoute,
} from "@shared/config/navigationRegistry";

type RouteAccessMap = Record<string, FeatureAvailabilityResult>;
type PhaseState = Record<RegulatoryPhase, boolean>;
type NavigationResolution = {
  phaseState: PhaseState;
  routeAccessMap: RouteAccessMap;
};

export type DiscoverModuleRoute = NavigationRoute & {
  isAccessible: boolean;
  accessResult?: FeatureAvailabilityResult;
};

const ADDON_FEATURE_KEY_MAP: Record<string, string> = {
  telehealth: "TELEHEALTH",
  advanced_analytics_pack: "ADVANCED_ANALYTICS",
  priority_support: "PRIORITY_SUPPORT",
};

const PHASE_SENTINEL_FEATURES: Record<2 | 3, string> = {
  2: "STATISTICAL_ANALYSIS",
  3: "ICU_EMU_MONITORING",
};

const DEFAULT_PHASE_STATE: PhaseState = {
  1: true,
  2: true,
  3: true,
};

function resolveFeatureKey(route: NavigationRoute): string {
  return (
    route.entitlementFeatureKey ||
    (route.addonKey ? ADDON_FEATURE_KEY_MAP[route.addonKey] : "") ||
    ""
  );
}

export type UseEntitledNavigationResult = {
  routes: NavigationRoute[];
  loading: boolean;
  getNavigationByArea: (area: NavArea) => NavigationRoute[];
  getDashboardQuickActions: () => NavigationRoute[];
  getDiscoverModuleRoutes: () => DiscoverModuleRoute[];
};

export function useEntitledNavigation(): UseEntitledNavigationResult {
  const localPreview = isLocalDevelopmentPreview();
  const { user } = useAuth();
  const {
    checkFeaturesAvailable,
    isFeatureEnabled,
    loading: featureFlagsLoading,
  } = useFeatureFlags();

  const userRole = user?.role;

  const roleAccessibleRoutes = useMemo(
    () =>
      NAVIGATION_ROUTES.filter((route) =>
        canAccessNavigationRoute(route, userRole, () => true),
      ),
    [userRole],
  );

  const needsFeatureResolution = useMemo(
    () =>
      roleAccessibleRoutes.some((route) => {
        const phase = route.phase ?? 1;
        return phase > 1 || resolveFeatureKey(route).length > 0;
      }),
    [roleAccessibleRoutes],
  );

  const resolutionQuery = useQuery<NavigationResolution>({
    queryKey: [
      "entitled-navigation",
      user?.id ?? "anon",
      roleAccessibleRoutes
        .map((route) => `${route.id}:${route.phase ?? 1}:${resolveFeatureKey(route)}`)
        .join("|"),
    ],
    enabled: !featureFlagsLoading && !!user?.id && needsFeatureResolution,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const routeFeatureKeys = roleAccessibleRoutes
        .map((route) => resolveFeatureKey(route))
        .filter(Boolean);
      const uniqueKeys = Array.from(
        new Set([
          PHASE_SENTINEL_FEATURES[2],
          PHASE_SENTINEL_FEATURES[3],
          ...routeFeatureKeys,
        ]),
      );
      const batchResults =
        uniqueKeys.length > 0
          ? await checkFeaturesAvailable(uniqueKeys)
          : ({} as Record<string, { available: boolean; reason?: string }>);

      const phaseState: PhaseState = {
        1: true,
        2:
          batchResults[PHASE_SENTINEL_FEATURES[2]]?.reason !==
          "FEATURE_DISABLED_REGULATORY",
        3:
          batchResults[PHASE_SENTINEL_FEATURES[3]]?.reason !==
          "FEATURE_DISABLED_REGULATORY",
      };

      const routeAccessMap = Object.fromEntries(
        roleAccessibleRoutes.map((route) => {
          const featureKey = resolveFeatureKey(route);
          if (!featureKey) {
            return [route.id, { available: true }] as const;
          }

          return [
            route.id,
            batchResults[featureKey] ?? {
              available: process.env.NODE_ENV === "development",
            },
          ] as const;
        }),
      );

      return { phaseState, routeAccessMap };
    },
  });

  const phaseState = resolutionQuery.data?.phaseState ?? DEFAULT_PHASE_STATE;

  const isPhaseEnabled = useMemo(
    () => (phase: RegulatoryPhase) => phaseState[phase],
    [phaseState],
  );

  const roleAndPhaseRoutes = useMemo(
    () =>
      roleAccessibleRoutes.filter((route) =>
        localPreview || canAccessNavigationRoute(route, userRole, isPhaseEnabled),
      ),
    [isPhaseEnabled, localPreview, roleAccessibleRoutes, userRole],
  );

  const routeAccessMap = resolutionQuery.data?.routeAccessMap ?? {};

  const routes = useMemo(
    () =>
      roleAndPhaseRoutes.filter((route) => {
        if (localPreview) return true;
        const featureKey = resolveFeatureKey(route);
        if (!featureKey) return true;
        return routeAccessMap[route.id]?.available === true;
      }),
    [localPreview, roleAndPhaseRoutes, routeAccessMap],
  );

  const discoverRoutes = useMemo(
    () =>
      roleAndPhaseRoutes.filter((route) => {
        if (!route.discoverModule) {
          return false;
        }

        return true;
      }).map((route) => {
        const featureKey = resolveFeatureKey(route);
        if (!featureKey) {
          return {
            ...route,
            isAccessible: true,
          };
        }

        const accessResult = routeAccessMap[route.id] ?? {
          available: isFeatureEnabled(featureKey),
        };

        return {
          ...route,
          isAccessible:
            accessResult.available === true || isFeatureEnabled(featureKey),
          accessResult,
        };
      }),
    [isFeatureEnabled, roleAndPhaseRoutes, routeAccessMap],
  );

  const getNavigationByArea = (area: NavArea) =>
    routes.filter((route) => route.area === area);

  const getDashboardQuickActions = () =>
    routes.filter((route) => route.dashboardQuickAction);

  const getDiscoverModuleRoutes = () =>
    discoverRoutes;

  const loading =
    featureFlagsLoading ||
    (needsFeatureResolution &&
      (resolutionQuery.isPending || resolutionQuery.isFetching));

  return {
    routes,
    loading,
    getNavigationByArea,
    getDashboardQuickActions,
    getDiscoverModuleRoutes,
  };
}

export default useEntitledNavigation;
